/**
 * Smart Auto-86 / Menu availability API
 * According to API docs: Auto-86 uses existing dish endpoints with new fields:
 * - sold_out_status (boolean)
 * - is_automatically_86ed (boolean)
 * - alternative_suggestion_item_id (string, optional)
 * 
 * Base URL: https://api.innoworx.ai/Stage
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

// Use api.innoworx.ai directly
const API_BASE_URL =
   'https://api.innoworx.ai/Stage';

const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (idToken.payload.exp > currentTime) return tokens.idToken.toString();
    }
  } catch (e) {
    console.error('SmartAuto API: auth error', e);
  }
  return null;
};

const STATUS = {
  AVAILABLE: 'available',
  SOLD_OUT_AUTO: 'sold_out_auto',
  SOLD_OUT_MANUAL: 'sold_out_manual',
};

/**
 * Get dishes by site (with Auto-86 fields).
 * POST /restaurant/dish/getall-by-site?site_id={site_id}
 * Lambda: innoworx-restaurant-getall-dishes-by-site-id
 * 
 * Note: API Gateway has POST method configured (not GET), even though Lambda supports both.
 * Returns dishes with: sold_out_status, is_automatically_86ed, alternative_suggestion_item_id
 */
const getDishesBySite = async (site_id) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  // site_id is required by the API
  if (!site_id) {
    throw new Error('Missing required query parameter: site_id. Please select a site.');
  }

  const params = new URLSearchParams();
  params.set('site_id', site_id);

  const url = `${API_BASE_URL}/restaurant/dish/getall-by-site?${params.toString()}`;
  
  let res;
  try {
    res = await fetch(url, {
      method: 'POST', // API Gateway has POST method configured (not GET)
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (fetchError) {
    // Handle CORS and network errors
    if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
      const corsError = new Error(
        `CORS Error: Unable to connect to the Restaurant API. ` +
        'The API Gateway needs to be configured to allow CORS from your origin (http://localhost:3000). ' +
        'Please contact the backend team to ensure CORS headers are properly configured for POST /restaurant/dish/getall-by-site.'
      );
      corsError.isCorsError = true;
      corsError.isPreflightIssue = true;
      
      // Log CORS/network error to logging service
      loggingService.logError(
        'api_error_auto86',
        `Auto-86 API network/CORS error for getDishesBySite`,
        corsError,
        {
          endpoint: '/restaurant/dish/getall-by-site',
          method: 'POST',
          site_id,
          errorType: 'CORS/NetworkError',
        }
      ).catch(logError => {
        console.warn('[smartAutoApi] Failed to log error:', logError);
      });
      
      throw corsError;
    }
    
    // Log other fetch errors
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 API fetch error for getDishesBySite`,
      fetchError,
      {
        endpoint: '/restaurant/dish/getall-by-site',
        method: 'POST',
        site_id,
        errorType: fetchError.name || 'FetchError',
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    throw fetchError;
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const errorMessage = errorData.error || errorData.message || `Failed to fetch dishes: ${res.status}`;
    
    let error;
    if (res.status === 400) {
      error = new Error(
        `Bad Request (400): ${errorMessage}. ` +
        'Please ensure all required parameters are provided.'
      );
    } else if (res.status === 403) {
      error = new Error(
        `Access forbidden (403): ${errorMessage}. ` +
        'Please verify your Authorization token is valid and includes the required Cognito attributes.'
      );
    } else {
      error = new Error(errorMessage);
    }
    
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/restaurant/dish/getall-by-site';
    error.responseData = errorData;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 API error: ${res.status} ${res.statusText} for getDishesBySite`,
      error,
      {
        endpoint: '/restaurant/dish/getall-by-site',
        method: 'POST',
        site_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errorData,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    throw error;
  }

  const data = await res.json();
  // Handle different response structures
  const dishes = Array.isArray(data) ? data : (data.dishes || data.items || []);
  
  // Log API event for successful fetch
  loggingService.logInfo(
    'api_success_auto86',
    `Successfully fetched dishes for site ${site_id}`,
    {
      endpoint: '/restaurant/dish/getall-by-site',
      method: 'POST',
      site_id,
      dishesCount: dishes.length,
    }
  ).catch(logError => {
    console.warn('[smartAutoApi] Failed to log success event:', logError);
  });
  
  return dishes;
};

/**
 * Get dish by ID (for resolving alternative suggestion names).
 * GET /restaurant/dish?dish_id={dish_id}
 */
const getDishById = async (dish_id) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  const url = `${API_BASE_URL}/restaurant/dish?dish_id=${dish_id}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const error = new Error(errorData.error || `Failed to fetch dish: ${res.status}`);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/restaurant/dish';
    error.responseData = errorData;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 API error: ${res.status} ${res.statusText} for getDishById`,
      error,
      {
        endpoint: '/restaurant/dish',
        method: 'GET',
        dish_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errorData,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    throw error;
  }

  const data = await res.json();
  
  // Log API event for successful fetch
  loggingService.logInfo(
    'api_success_auto86',
    `Successfully fetched dish ${dish_id}`,
    {
      endpoint: '/restaurant/dish',
      method: 'GET',
      dish_id,
    }
  ).catch(logError => {
    console.warn('[smartAutoApi] Failed to log success event:', logError);
  });
  
  return data;
};

/**
 * Get stock items at/below critical threshold (for at-risk items).
 * GET /stock/items?site_id={site_id}&is_active=true
 * Filter client-side for items at or below critical_threshold
 */
const getAtRiskStockItems = async (site_id) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  params.set('is_active', 'true');
  params.set('limit', '100');

  const url = `${API_BASE_URL}/stock/items?${params.toString()}`;
  
  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (fetchError) {
    // Handle CORS and network errors
    if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
      const corsError = new Error(
        `CORS Error: Unable to connect to the Stock API. ` +
        'Please contact the backend team to ensure CORS headers are properly configured.'
      );
      corsError.isCorsError = true;
      
      // Log CORS/network error to logging service
      loggingService.logError(
        'api_error_auto86',
        `Auto-86 API network/CORS error for getAtRiskStockItems`,
        corsError,
        {
          endpoint: '/stock/items',
          method: 'GET',
          site_id,
          errorType: 'CORS/NetworkError',
        }
      ).catch(logError => {
        console.warn('[smartAutoApi] Failed to log error:', logError);
      });
      
      throw corsError;
    }
    
    // Log other fetch errors
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 API fetch error for getAtRiskStockItems`,
      fetchError,
      {
        endpoint: '/stock/items',
        method: 'GET',
        site_id,
        errorType: fetchError.name || 'FetchError',
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    throw fetchError;
  }

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const errorMessage = errorData.error || errorData.message || `Failed to fetch stock items: ${res.status}`;
    const error = new Error(errorMessage);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = '/stock/items';
    error.responseData = errorData;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 API error: ${res.status} ${res.statusText} for getAtRiskStockItems`,
      error,
      {
        endpoint: '/stock/items',
        method: 'GET',
        site_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errorData,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    throw error;
  }

  const data = await res.json();
  const items = Array.isArray(data) ? data : (data.items || []);
  
  // Filter items at or below critical threshold (but not zero)
  const atRiskItems = items.filter(item => {
    const currentQty = item.current_quantity || 0;
    const critical = item.critical_threshold || 0;
    return currentQty > 0 && currentQty <= critical;
  });
  
  // Log API event for successful fetch
  loggingService.logInfo(
    'api_success_auto86',
    `Successfully fetched at-risk stock items for site ${site_id}`,
    {
      endpoint: '/stock/items',
      method: 'GET',
      site_id,
      atRiskItemsCount: atRiskItems.length,
    }
  ).catch(logError => {
    console.warn('[smartAutoApi] Failed to log success event:', logError);
  });
  
  return atRiskItems;
};

/**
 * Get Auto-86 / menu availability dashboard data.
 * Aggregates dishes by site and calculates summary statistics.
 */
export const getAuto86Dashboard = async ({ site_id, status_filter } = {}) => {
  try {
    // site_id is required - return empty data if not provided
    if (!site_id) {
      return {
        success: false,
        message: 'Please select a site to view Auto-86 dashboard.',
        data: {
          summary: {
            dishes_sold_out_auto: 0,
            dishes_sold_out_manual: 0,
            items_at_or_below_critical: 0,
            last_auto86_run_at: null,
          },
          dishes: [],
          at_risk_items: [],
        },
      };
    }

    // Fetch dishes for the selected site
    const allDishes = await getDishesBySite(site_id);
    
    // Resolve alternative suggestion names
    const dishesWithAlternatives = await Promise.all(
      allDishes.map(async (dish) => {
        let alternative_suggestion_name = null;
        if (dish.alternative_suggestion_item_id) {
          try {
            const altDish = await getDishById(dish.alternative_suggestion_item_id);
            alternative_suggestion_name = altDish.dish_name || altDish.name || null;
          } catch (err) {
            console.warn(`Failed to resolve alternative dish ${dish.alternative_suggestion_item_id}:`, err);
          }
        }

        // Determine status from API fields
        let status = STATUS.AVAILABLE;
        if (dish.sold_out_status) {
          status = dish.is_automatically_86ed ? STATUS.SOLD_OUT_AUTO : STATUS.SOLD_OUT_MANUAL;
        }

        // Get reason (could be from stock items or manual entry)
        // Backend should provide this, but we'll use a default if missing
        const reason = dish.sold_out_reason || dish.reason || (dish.is_automatically_86ed ? 'Stock at critical' : 'Manual');

        return {
          dish_id: dish.dish_id || dish.id,
          dish_name: dish.dish_name || dish.name,
          site_id: dish.site_id,
          site_name: dish.site_name || dish.site?.site_name || dish.site?.name,
          status,
          reason,
          alternative_suggestion_name,
          alternative_suggestion_id: dish.alternative_suggestion_item_id,
          production_template_id: dish.production_template_id,
          last_updated: dish.updated_at || dish.last_updated || dish.created_at,
        };
      })
    );

    // Apply status filter
    let filtered = dishesWithAlternatives;
    if (status_filter === 'sold_out') {
      filtered = filtered.filter((d) => d.status === STATUS.SOLD_OUT_AUTO || d.status === STATUS.SOLD_OUT_MANUAL);
    } else if (status_filter === 'available') {
      filtered = filtered.filter((d) => d.status === STATUS.AVAILABLE);
    }

    // Calculate summary
    const soldOutAuto = dishesWithAlternatives.filter((d) => d.status === STATUS.SOLD_OUT_AUTO).length;
    const soldOutManual = dishesWithAlternatives.filter((d) => d.status === STATUS.SOLD_OUT_MANUAL).length;

    // Fetch at-risk items
    let atRiskItems = [];
    try {
      const stockItems = await getAtRiskStockItems(site_id || undefined);
      atRiskItems = stockItems.map((item) => ({
        item_id: item.stock_item_id || item.item_id || item.id,
        item_name: item.item_name || item.name,
        current_quantity: item.current_quantity || 0,
        critical_threshold: item.critical_threshold || 0,
        reorder_threshold: item.reorder_threshold || 0,
        site_id: item.site_id,
        site_name: item.site_name || item.site?.site_name || item.site?.name,
        dishes_affected: item.dishes_affected || [], // Backend should populate this
      }));
    } catch (err) {
      console.warn('Failed to fetch at-risk items:', err);
    }

    const result = {
      success: true,
      data: {
        summary: {
          dishes_sold_out_auto: soldOutAuto,
          dishes_sold_out_manual: soldOutManual,
          items_at_or_below_critical: atRiskItems.length,
          last_auto86_run_at: new Date().toISOString(), // Backend should provide this
        },
        dishes: filtered,
        at_risk_items: atRiskItems,
      },
    };
    
    // Log API event for successful dashboard fetch
    loggingService.logInfo(
      'api_success_auto86',
      `Successfully fetched Auto-86 dashboard for site ${site_id}`,
      {
        endpoint: 'getAuto86Dashboard',
        method: 'GET',
        site_id,
        status_filter,
        dishesCount: filtered.length,
        atRiskItemsCount: atRiskItems.length,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log success event:', logError);
    });
    
    return result;
  } catch (err) {
    console.error('Error fetching Auto-86 dashboard:', err);
    
    // Provide more specific error messages for common issues
    let errorMessage = err?.message || 'Network error';
    
    if (err?.isCorsError) {
      errorMessage = 
        'CORS Error: The API endpoint is not configured to allow requests from this origin (http://localhost:3000). ' +
        'The backend team needs to configure CORS headers for POST /restaurant/dish/getall-by-site endpoint. ' +
        'This requires: Access-Control-Allow-Origin header and proper OPTIONS preflight handling.';
    } else if (err?.message?.includes('403')) {
      errorMessage = 
        'Access forbidden (403). Please verify your Authorization token is valid and includes the required Cognito attributes.';
    } else if (err?.message?.includes('404')) {
      errorMessage = 
        'Endpoint not found (404). The dish endpoint may not be implemented yet. ' +
        'Please check with the backend team.';
    }
    
    // Log error to logging service
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 dashboard error: ${errorMessage}`,
      err,
      {
        site_id,
        status_filter,
        errorType: err?.name || 'Error',
        isCorsError: err?.isCorsError || false,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    // Return empty data on error
    return { 
      success: false, 
      message: errorMessage, 
      data: {
        summary: {
          dishes_sold_out_auto: 0,
          dishes_sold_out_manual: 0,
          items_at_or_below_critical: 0,
          last_auto86_run_at: null,
        },
        dishes: [],
        at_risk_items: [],
      }
    };
  }
};

/**
 * PUT /restaurant/dish/{dish_id}
 * Lambda: innoworx-restaurant-dish-update
 * Updates dish fields (sold_out_status, is_automatically_86ed, alternative_suggestion_item_id, etc.)
 */
const updateDish = async (dish_id, body) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing');
  }
  if (!dish_id) {
    throw new Error('dish_id is required');
  }
  const url = `${API_BASE_URL}/restaurant/dish/${encodeURIComponent(dish_id)}`;
  let res;
  try {
    res = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (fetchErr) {
    if (fetchErr.name === 'TypeError' && fetchErr.message.includes('Failed to fetch')) {
      const corsError = new Error(
        'Request blocked (CORS). The API must allow PUT and OPTIONS for /restaurant/dish/{id} from your origin (e.g. http://localhost:3000). Ask the backend team to enable CORS for this endpoint.'
      );
      corsError.isCorsError = true;
      
      // Log CORS/network error to logging service
      loggingService.logError(
        'api_error_auto86',
        `Auto-86 API network/CORS error for updateDish`,
        corsError,
        {
          endpoint: `/restaurant/dish/${dish_id}`,
          method: 'PUT',
          dish_id,
          errorType: 'CORS/NetworkError',
        }
      ).catch(logError => {
        console.warn('[smartAutoApi] Failed to log error:', logError);
      });
      
      throw corsError;
    }
    
    // Log other fetch errors
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 API fetch error for updateDish`,
      fetchErr,
      {
        endpoint: `/restaurant/dish/${dish_id}`,
        method: 'PUT',
        dish_id,
        errorType: fetchErr.name || 'FetchError',
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    throw fetchErr;
  }
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const message = errorData.error || errorData.message || `Update failed: ${res.status}`;
    const error = new Error(message);
    error.status = res.status;
    error.statusText = res.statusText;
    error.endpoint = `/restaurant/dish/${dish_id}`;
    error.responseData = errorData;
    
    // Log API error to logging service
    loggingService.logError(
      'api_error_auto86',
      `Auto-86 API error: ${res.status} ${res.statusText} for updateDish`,
      error,
      {
        endpoint: `/restaurant/dish/${dish_id}`,
        method: 'PUT',
        dish_id,
        status: res.status,
        statusText: res.statusText,
        responseData: errorData,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log error:', logError);
    });
    
    throw error;
  }
  const data = await res.json().catch(() => ({}));
  
  // Log API event for successful update
  loggingService.logInfo(
    'api_success_auto86',
    `Successfully updated dish ${dish_id}`,
    {
      endpoint: `/restaurant/dish/${dish_id}`,
      method: 'PUT',
      dish_id,
      updatedFields: Object.keys(body || {}),
    }
  ).catch(logError => {
    console.warn('[smartAutoApi] Failed to log success event:', logError);
  });
  
  return data;
};

/**
 * Mark a dish as available again (manual override).
 * PUT /restaurant/dish/{dish_id} with body:
 *   { sold_out_status: false, is_automatically_86ed: false, sold_out_reason: "" }
 */
export const markDishAvailable = async ({ dish_id, site_id } = {}) => {
  if (!dish_id) {
    return { success: false, message: 'dish_id is required' };
  }
  try {
    await updateDish(dish_id, {
      sold_out_status: false,
      is_automatically_86ed: false,
      sold_out_reason: '',
    });
    
    // Log API event for marking dish available
    loggingService.logInfo(
      'api_success_auto86',
      `Successfully marked dish ${dish_id} as available`,
      {
        endpoint: `/restaurant/dish/${dish_id}`,
        method: 'PUT',
        dish_id,
        site_id,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log success event:', logError);
    });
    
    return { success: true };
  } catch (err) {
    return {
      success: false,
      message: err.message || 'Failed to mark dish as available.',
    };
  }
};

/**
 * Update alternative suggestion for a dish.
 * PUT /restaurant/dish/{dish_id} with body:
 *   { alternative_suggestion_item_id: "<ALT_DISH_ID>" }
 * Pass alternative_dish_id as null or "" to clear the alternative.
 */
export const setAlternativeSuggestion = async ({ dish_id, site_id, alternative_dish_id } = {}) => {
  if (!dish_id) {
    return { success: false, message: 'dish_id is required' };
  }
  try {
    const body = {
      alternative_suggestion_item_id: alternative_dish_id || null,
    };
    await updateDish(dish_id, body);
    
    // Log API event for setting alternative suggestion
    loggingService.logInfo(
      'api_success_auto86',
      `Successfully updated alternative suggestion for dish ${dish_id}`,
      {
        endpoint: `/restaurant/dish/${dish_id}`,
        method: 'PUT',
        dish_id,
        site_id,
        alternativeDishId: alternative_dish_id,
      }
    ).catch(logError => {
      console.warn('[smartAutoApi] Failed to log success event:', logError);
    });
    
    return { success: true };
  } catch (err) {
    return {
      success: false,
      message: err.message || 'Failed to update alternative suggestion.',
    };
  }
};

// ——— Mock data for development / when backend not ready ——— COMMENTED OUT

// function getMockAuto86Dashboard({ site_id, status_filter } = {}) {
//   const dishes = [
//     { dish_id: 'd1', dish_name: 'Paneer Sandwich', site_id: 'site-001', site_name: 'Koramangala Outlet', status: STATUS.SOLD_OUT_AUTO, reason: 'Tomatoes at 0', alternative_suggestion_name: 'Veg Burger', alternative_suggestion_id: 'd2', production_template_id: 'pt1', last_updated: new Date(Date.now() - 15 * 60000).toISOString() },
//     { dish_id: 'd2', dish_name: 'Veg Burger', site_id: 'site-001', site_name: 'Koramangala Outlet', status: STATUS.AVAILABLE, reason: null, alternative_suggestion_name: null, alternative_suggestion_id: null, production_template_id: 'pt2', last_updated: new Date(Date.now() - 60 * 60000).toISOString() },
//     { dish_id: 'd3', dish_name: 'French Fries', site_id: 'site-001', site_name: 'Koramangala Outlet', status: STATUS.SOLD_OUT_MANUAL, reason: 'Manual', alternative_suggestion_name: 'Veg Burger', alternative_suggestion_id: 'd2', production_template_id: 'pt3', last_updated: new Date(Date.now() - 30 * 60000).toISOString() },
//     { dish_id: 'd4', dish_name: 'Pizza Margherita', site_id: 'site-002', site_name: 'Indiranagar Branch', status: STATUS.SOLD_OUT_AUTO, reason: 'Mozzarella at critical', alternative_suggestion_name: 'Coffee', alternative_suggestion_id: 'd5', production_template_id: 'pt4', last_updated: new Date(Date.now() - 10 * 60000).toISOString() },
//     { dish_id: 'd5', dish_name: 'Coffee', site_id: 'site-002', site_name: 'Indiranagar Branch', status: STATUS.AVAILABLE, reason: null, alternative_suggestion_name: null, alternative_suggestion_id: null, production_template_id: null, last_updated: new Date(Date.now() - 120 * 60000).toISOString() },
//   ];

//   let filtered = dishes;
//   if (site_id) filtered = filtered.filter((d) => d.site_id === site_id);
//   if (status_filter === 'sold_out') {
//     filtered = filtered.filter((d) => d.status === STATUS.SOLD_OUT_AUTO || d.status === STATUS.SOLD_OUT_MANUAL);
//   } else if (status_filter === 'available') {
//     filtered = filtered.filter((d) => d.status === STATUS.AVAILABLE);
//   }

//   const soldOutAuto = dishes.filter((d) => d.status === STATUS.SOLD_OUT_AUTO).length;
//   const soldOutManual = dishes.filter((d) => d.status === STATUS.SOLD_OUT_MANUAL).length;
//   const atCriticalCount = 3; // items at or below critical threshold

//   return {
//     summary: {
//       dishes_sold_out_auto: soldOutAuto,
//       dishes_sold_out_manual: soldOutManual,
//       items_at_or_below_critical: atCriticalCount,
//       last_auto86_run_at: new Date(Date.now() - 5 * 60000).toISOString(),
//     },
//     dishes: filtered,
//     at_risk_items: [
//       { item_id: 'st1', item_name: 'Tomatoes', current_quantity: 8, critical_threshold: 10, reorder_threshold: 20, dishes_affected: ['Paneer Sandwich', 'Salad'], site_id: 'site-001', site_name: 'Koramangala Outlet' },
//       { item_id: 'st2', item_name: 'Mozzarella', current_quantity: 5, critical_threshold: 5, reorder_threshold: 15, dishes_affected: ['Pizza Margherita', 'Pizza Pepperoni'], site_id: 'site-002', site_name: 'Indiranagar Branch' },
//     ],
//   };
// }

export const STATUS_VALUES = STATUS;
export default {
  getAuto86Dashboard,
  markDishAvailable,
  setAlternativeSuggestion,
  STATUS_VALUES: STATUS,
};
